import { usePortal } from "@gorhom/portal";
import { CheckCircle } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, YStack } from "tamagui";
import { usePlanNavigation } from "../../../../plans/usePlanNavigation";
import { Button } from "../../../../ui/Button";
import type ExpenseItemModule from "../../data/ExpenseItemModel";
import type ExpenseModuleModel from "../../ExpenseModuleModel";
import { useCalculator } from "../Calculator";
import { AddExpenseFormAmount } from "./AddExpenseFormAmount";
import { AddExpenseFormPaidBy } from "./AddExpenseFormPaidBy";
import { AddExpenseFormSplits } from "./AddExpenseFormSplits";
import { AddExpenseFormTitle } from "./AddExpenseFormTitle";
import { ExpenseFormState } from "./ExpenseFormState";

export const AddExpenseForm: React.FC<{
  module: ExpenseModuleModel;
  expense?: ExpenseItemModule;
}> = observer(({ module, expense }) => {
  const user = module.plan.userInPlan;
  const { state: calculatorState, Calculator } = useCalculator(0, {
    expanded: false,
  });
  const navigation = usePlanNavigation();

  const insets = useSafeAreaInsets();

  const [formState] = useState(
    () =>
      new ExpenseFormState(
        user?.uid ?? "",
        module.plan.users.map((user) => ({ userId: user.uid, amount: 0 })),
        expense,
      ),
  );

  useEffect(() => {
    module.plan.setShowComments(false);

    return () => module.plan.setShowComments(true);
  }, [module]);

  const { updatePortal } = usePortal("bottomContent");

  const handleSave = useCallback(async () => {
    const data = formState.toObject();

    if (!module.plan.userInPlan) {
      return;
    }

    try {
      if (expense) {
        await expense.update({ ...data, title: data.title ?? "" });
      } else {
        await module.addExpense({
          title: data.title ?? "",
          amount: data.amount,
          paidByUserId: data.paidByUserId ?? module.plan.userInPlan.uid,
          splits: data.splits,
        });
      }

      navigation.navigate("Module", {
        planUid: module.plan.uid,
        moduleUid: module.uid,
      });
    } catch (e) {
      console.error(e);
    }
  }, [formState, module, navigation, expense]);

  useEffect(() => {
    if (!module) {
      return;
    }

    updatePortal(
      "calculator",
      <View
        width="100%"
        justifyContent="center"
        alignItems="center"
        paddingBottom={insets.bottom}
        backgroundColor={"$backgroundBlock"}
      >
        <YStack gap={1} maxWidth={600} width="100%">
          <Calculator />
          <Button onPress={handleSave} icon={CheckCircle}>
            {expense ? "Update expense" : "Add expense"}
          </Button>
        </YStack>
      </View>,
    );

    return () => updatePortal("calculator", null);
  }, [module, updatePortal, Calculator, handleSave, expense, insets]);

  if (!user) {
    return null;
  }

  return (
    <YStack gap="$medium" onPress={() => calculatorState.setExpanded(false)}>
      <AddExpenseFormPaidBy formState={formState} />
      <AddExpenseFormAmount
        module={module}
        calculatorState={calculatorState}
        formState={formState}
      />
      <AddExpenseFormTitle formState={formState} />
      <AddExpenseFormSplits
        module={module}
        calculatorState={calculatorState}
        formState={formState}
      />
    </YStack>
  );
});
